<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import { useI18n } from 'vue-i18n'
import clubService from '../services/club.service'

const route = useRoute()
const authStore = useAuthStore()
const toastStore = useToastStore()
const { t } = useI18n()

const getCountryName = (code: string) => {
  const key = `countries.${code}`
  const translated = t(key)
  return translated !== key ? translated : code
}

const showEditDropdown = ref(false)
const loading = ref(true)
const loadingBankAccounts = ref(false)
const loadingSupervisors = ref(false)

// Get club UUID from route
const clubUuid = computed(() => route.params.uuid as string)

// Get current role
const currentRole = computed(() => authStore.initData?.persons?.current?.role || null)

// Check if user can edit club (OWNER or SUPERVISOR)
const canEditClub = computed(() => {
  return currentRole.value === 'OWNER' || currentRole.value === 'SUPERVISOR'
})

// Club data
const clubData = ref<any>(null)
const bankAccounts = ref<any[]>([])
const supervisors = ref<any[]>([])

// Fetch club data
const fetchClubData = async () => {
  if (!clubUuid.value) return

  loading.value = true

  try {
    const response = await clubService.getClub(clubUuid.value)
    clubData.value = response.club
  } catch (err: any) {
    console.error('Error fetching club data:', err)
    toastStore.error(t('club.errorFetchingClub'))
  } finally {
    loading.value = false
  }
}

// Fetch bank accounts
const fetchBankAccounts = async () => {
  if (!clubUuid.value) return

  loadingBankAccounts.value = true

  try {
    const response = await clubService.getBankAccounts(clubUuid.value)
    bankAccounts.value = response.bank_accounts || []
  } catch (err: any) {
    console.error('Error fetching bank accounts:', err)
    toastStore.error(t('club.errorFetchingBankAccounts'))
  } finally {
    loadingBankAccounts.value = false
  }
}

// Fetch supervisors
const fetchSupervisors = async () => {
  if (!clubUuid.value) return

  loadingSupervisors.value = true

  try {
    const response = await clubService.getSupervisors(clubUuid.value)
    supervisors.value = response.supervisors || []
  } catch (err: any) {
    console.error('Error fetching supervisors:', err)
    toastStore.error(t('club.errorFetchingSupervisors'))
  } finally {
    loadingSupervisors.value = false
  }
}

// Format account number (add spaces every 4 digits)
const formatAccountNumber = (accountNumber: string): string => {
  if (!accountNumber) return ''
  return accountNumber.replace(/(.{4})/g, '$1 ').trim()
}

// Initialize
onMounted(() => {
  fetchClubData()
  fetchBankAccounts()
  fetchSupervisors()
})

// Copy bank account success states
const copySuccessStates = ref<Record<number, boolean>>({})

// Toggle edit dropdown
const toggleEditDropdown = () => {
  showEditDropdown.value = !showEditDropdown.value
}

// Close dropdown when clicking outside
const closeDropdown = () => {
  showEditDropdown.value = false
}

// Copy email
const copyEmail = async () => {
  try {
    await navigator.clipboard.writeText(clubData.value.contact.email)
    toastStore.success(t('club.emailCopied'))
  } catch (err) {
    console.error(t('club.errorCopying'), err)
  }
}

// Copy bank account number
const copyBankAccount = async (accountNumber: string, accountId: number) => {
  try {
    await navigator.clipboard.writeText(accountNumber)
    copySuccessStates.value[accountId] = true

    // Hide success message after 2 seconds
    setTimeout(() => {
      copySuccessStates.value[accountId] = false
    }, 2000)
  } catch (err) {
    console.error('Copy error:', err)
  }
}

// Copy supervisor email
const copySupervisorEmail = async (email: string) => {
  if (!email || email === '-') return

  try {
    await navigator.clipboard.writeText(email)
    toastStore.success(t('club.emailCopied'))
  } catch (err) {
    console.error('Copy error:', err)
    toastStore.error(t('club.errorCopying'))
  }
}

// Copy supervisor phone
const copySupervisorPhone = async (phone: string) => {
  if (!phone || phone === '-') return

  try {
    await navigator.clipboard.writeText(phone)
    toastStore.success(t('club.phoneCopied'))
  } catch (err) {
    console.error('Copy error:', err)
    toastStore.error(t('club.errorCopying'))
  }
}

// Add supervisor modal state
const showAddSupervisorModal = ref(false)
const supervisorForm = ref({
  first_name: '',
  last_name: '',
  email: '',
  phone: ''
})
const supervisorFormErrors = ref<Record<string, string[]>>({})
const savingSupervisor = ref(false)

// Open add supervisor modal
const openAddSupervisorModal = () => {
  supervisorForm.value = {
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  }
  supervisorFormErrors.value = {}
  showAddSupervisorModal.value = true
}

// Close add supervisor modal
const closeAddSupervisorModal = () => {
  showAddSupervisorModal.value = false
  supervisorForm.value = {
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  }
  supervisorFormErrors.value = {}
}

// Save supervisor
const saveSupervisor = async () => {
  if (!clubUuid.value) return

  supervisorFormErrors.value = {}
  savingSupervisor.value = true

  try {
    await clubService.addSupervisor(clubUuid.value, supervisorForm.value)
    toastStore.success(t('club.supervisorAddedSuccessfully'))
    closeAddSupervisorModal()
    await fetchSupervisors()
  } catch (err: any) {
    console.error('Error adding supervisor:', err)

    if (err.response?.data?.errors) {
      supervisorFormErrors.value = err.response.data.errors
    } else {
      toastStore.error(err.response?.data?.message || t('club.errorAddingSupervisor'))
    }
  } finally {
    savingSupervisor.value = false
  }
}

// Get validation error for field
const getSupervisorFieldError = (field: string): string | null => {
  return supervisorFormErrors.value[field]?.[0] || null
}

// Remove supervisor modal state
const showRemoveSupervisorModal = ref(false)
const supervisorToRemove = ref<any>(null)
const removingSupervisor = ref(false)

// Open remove supervisor modal
const openRemoveSupervisorModal = (supervisor: any) => {
  supervisorToRemove.value = supervisor
  showRemoveSupervisorModal.value = true
}

// Close remove supervisor modal
const closeRemoveSupervisorModal = () => {
  showRemoveSupervisorModal.value = false
  supervisorToRemove.value = null
}

// Confirm remove supervisor
const confirmRemoveSupervisor = async () => {
  if (!clubUuid.value || !supervisorToRemove.value) return

  removingSupervisor.value = true

  try {
    await clubService.removeSupervisor(clubUuid.value, supervisorToRemove.value.uuid)
    toastStore.success(t('club.supervisorRemovedSuccessfully'))
    closeRemoveSupervisorModal()
    await fetchSupervisors()
  } catch (err: any) {
    console.error('Error removing supervisor:', err)
    toastStore.error(err.response?.data?.message || t('club.errorRemovingSupervisor'))
  } finally {
    removingSupervisor.value = false
  }
}

// Resend supervisor invitation
const resendSupervisorInvitation = async (supervisor: any) => {
  if (!clubUuid.value) return

  try {
    await clubService.resendSupervisorInvitation(clubUuid.value, supervisor.uuid)
    toastStore.success(t('club.invitationSent'))
  } catch (err: any) {
    console.error('Error resending supervisor invitation:', err)
    toastStore.error(err.response?.data?.message || t('club.errorSendingInvitation'))
  }
}
</script>

<template>
  <main class="bg-gray-50 min-h-screen" @click="closeDropdown">
    <div class="max-w-7xl mx-auto px-4 py-4">
      <!-- Page Header -->
      <div class="mb-4 flex md:flex-row flex-col md:justify-between md:items-start gap-2">
        <div>
          <h1 class="text-2xl font-bold text-gray-900">{{ t('club.title') }}</h1>
          <p class="text-sm text-gray-600 mt-1">{{ t('club.subtitle') }}</p>
        </div>
        <div v-if="canEditClub" class="relative self-end md:self-auto">
          <button
            @click.stop="toggleEditDropdown"
            class="px-4 py-2 bg-blue-600 text-sm font-medium text-white rounded hover:bg-blue-700 border-2 border-blue-600 flex items-center gap-2"
          >
            {{ t('club.editClubData') }}
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
          <div
            v-show="showEditDropdown"
            class="absolute right-0 mt-2 w-64 bg-white border-2 border-gray-300 rounded shadow-lg z-50"
            @click.stop
          >
            <router-link :to="`/club/${clubUuid}/edit`" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-b border-gray-200">{{ t('club.editClubDataTitle') }}</router-link>
            <router-link :to="`/club/${clubUuid}/payment-settings`" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-b border-gray-200">{{ t('club.editPaymentSettings') }}</router-link>
            <router-link :to="`/club/${clubUuid}/bank-accounts`" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-b border-gray-200">{{ t('club.editBankAccounts') }}</router-link>
            <router-link :to="`/club/${clubUuid}/payment-reminder-template`" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">{{ t('club.editPaymentReminderTemplate') }}</router-link>
          </div>
        </div>
      </div>

      <!-- Club Info Card -->
      <div v-if="!loading && clubData" class="bg-white border-2 border-gray-300 rounded shadow-sm p-4 mb-4">
        <div class="flex flex-col md:flex-row gap-4">
          <!-- Logo Section -->
          <div class="flex-shrink-0">
            <div class="w-32 h-32 border-2 border-gray-300 rounded flex items-center justify-center bg-white">
              <img
                v-if="clubData.logo_url"
                :src="clubData.logo_url"
                :alt="t('club.logo')"
                class="w-full h-full object-contain p-2"
              >
              <div v-else class="text-gray-400 text-xs text-center">{{ t('club.noLogo') }}</div>
            </div>
          </div>

          <!-- Club Details -->
          <div class="flex-1">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <!-- Nazwa klubu -->
              <div>
                <label class="block text-xs font-medium text-gray-600 mb-1">{{ t('club.clubName') }}</label>
                <p class="text-sm font-semibold text-gray-900">{{ clubData.name }}</p>
              </div>

              <!-- Skrót nazwy -->
              <div>
                <label class="block text-xs font-medium text-gray-600 mb-1">{{ t('club.shortName') }}</label>
                <p class="text-sm font-semibold text-gray-900">{{ clubData.short_name }}</p>
              </div>

              <!-- Przydomek -->
              <div>
                <label class="block text-xs font-medium text-gray-600 mb-1">{{ t('club.nickname') }}</label>
                <p class="text-sm text-gray-900">{{ clubData.nickname }}</p>
              </div>

              <!-- NIP -->
              <div>
                <label class="block text-xs font-medium text-gray-600 mb-1">{{ t('club.nip') }}</label>
                <p class="text-sm text-gray-900">{{ clubData.nip }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Address Section -->
      <div v-if="!loading && clubData" class="bg-white border-2 border-gray-300 rounded shadow-sm p-4 mb-4">
        <h2 class="text-lg font-semibold text-gray-800 mb-3">{{ t('club.address') }}</h2>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">{{ t('club.street') }}</label>
            <p class="text-sm text-gray-900">{{ clubData.address.street }}</p>
          </div>

          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">{{ t('club.buildingNumber') }}</label>
            <p class="text-sm text-gray-900">{{ clubData.address.building_number }}</p>
          </div>

          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">{{ t('club.city') }}</label>
            <p class="text-sm text-gray-900">{{ clubData.address.city }}</p>
          </div>

          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">{{ t('club.postalCode') }}</label>
            <p class="text-sm text-gray-900">{{ clubData.address.postal_code }}</p>
          </div>

          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">{{ t('club.country') }}</label>
            <p class="text-sm text-gray-900">{{ getCountryName(clubData.address.country) }}</p>
          </div>
        </div>
      </div>

      <!-- Supervisors Section -->
      <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-4 mb-4">
        <div class="flex justify-between items-center mb-3">
          <h2 class="text-lg font-semibold text-gray-800">{{ t('club.supervisors') }}</h2>
          <button
            v-if="currentRole === 'OWNER'"
            @click="openAddSupervisorModal"
            class="px-3 py-1.5 bg-green-600 text-sm font-medium text-white rounded hover:bg-green-700 border-2 border-green-600 flex items-center gap-2"
          >
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"/>
            </svg>
            {{ t('club.addSupervisor') }}
          </button>
        </div>

        <div v-if="loadingSupervisors" class="text-center py-4">
          <p class="text-gray-500">{{ t('club.loadingSupervisors') }}</p>
        </div>

        <div v-else-if="supervisors.length === 0" class="text-center py-4">
          <p class="text-gray-500">{{ t('club.noSupervisors') }}</p>
        </div>

        <div v-else class="space-y-3">
          <div
            v-for="supervisor in supervisors"
            :key="supervisor.uuid"
            class="p-3 border border-gray-300 rounded bg-gray-50"
          >
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label class="block text-xs font-medium text-gray-600 mb-1">{{ t('club.firstName') }}</label>
                <p class="text-sm font-semibold text-gray-900">{{ supervisor.first_name }}</p>
              </div>

              <div>
                <label class="block text-xs font-medium text-gray-600 mb-1">{{ t('club.lastName') }}</label>
                <p class="text-sm font-semibold text-gray-900">{{ supervisor.last_name }}</p>
              </div>

              <div>
                <label class="block text-xs font-medium text-gray-600 mb-1">{{ t('club.role') }}</label>
                <div class="flex items-center gap-2">
                  <span v-if="supervisor.role === 'OWNER'" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                    {{ t('club.owner') }}
                  </span>
                  <span v-else-if="supervisor.role === 'SUPERVISOR'" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                    {{ t('club.supervisor') }}
                  </span>
                  <button
                    v-if="currentRole === 'OWNER' && supervisor.role === 'SUPERVISOR'"
                    @click="openRemoveSupervisorModal(supervisor)"
                    class="px-2 py-1 bg-red-600 text-xs font-medium text-white rounded hover:bg-red-700 border border-red-600"
                    :title="t('club.removeSupervisor')"
                  >
                    {{ t('club.remove') }}
                  </button>
                </div>
              </div>

              <div class="space-y-3">
                <!-- Email -->
                <div>
                  <label class="block text-xs font-medium text-gray-600 mb-1">{{ t('club.email') }}</label>
                  <div class="flex items-center gap-2">
                    <p class="text-sm text-gray-900 flex-1 break-all">{{ supervisor.email || '-' }}</p>
                    <button
                      v-if="supervisor.email && supervisor.email !== '-'"
                      @click="copySupervisorEmail(supervisor.email)"
                      class="px-2 py-1 bg-gray-100 text-xs font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300 whitespace-nowrap flex-shrink-0"
                      :title="t('club.copyEmail')"
                    >
                      {{ t('club.copy') }}
                    </button>
                  </div>
                </div>

                <!-- Phone -->
                <div>
                  <label class="block text-xs font-medium text-gray-600 mb-1">{{ t('club.phone') }}</label>
                  <div class="flex items-center gap-2">
                    <p class="text-sm text-gray-900 flex-1">{{ supervisor.phone || '-' }}</p>
                    <button
                      v-if="supervisor.phone && supervisor.phone !== '-'"
                      @click="copySupervisorPhone(supervisor.phone)"
                      class="px-2 py-1 bg-gray-100 text-xs font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300 whitespace-nowrap flex-shrink-0"
                      :title="t('club.copyPhone')"
                    >
                      {{ t('club.copy') }}
                    </button>
                  </div>
                </div>

                <!-- Registration Status (only for OWNER and unregistered supervisors) -->
                <div v-if="currentRole === 'OWNER' && !supervisor.is_registered && supervisor.role === 'SUPERVISOR'">
                  <p class="text-xs text-orange-600 font-medium mb-2">{{ t('club.userNotRegistered') }}</p>
                  <button
                    v-if="supervisor.email && supervisor.email !== '-'"
                    @click="resendSupervisorInvitation(supervisor)"
                    class="px-3 py-1.5 bg-blue-600 text-xs font-medium text-white rounded hover:bg-blue-700 border border-blue-600"
                  >
                    {{ t('club.sendInvitation') }}
                  </button>
                  <p v-else class="text-xs text-red-600 font-medium">{{ t('club.noEmail') }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Bank Accounts Section -->
      <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-4 mb-4">
        <h2 class="text-lg font-semibold text-gray-800 mb-3">{{ t('club.bankAccounts') }}</h2>

        <div v-if="loadingBankAccounts" class="text-center py-4">
          <p class="text-gray-500">{{ t('club.loadingBankAccounts') }}</p>
        </div>

        <div v-else-if="bankAccounts.length === 0" class="text-center py-4">
          <p class="text-gray-500">{{ t('club.noBankAccounts') }}</p>
        </div>

        <div v-else class="space-y-4">
          <!-- Bank Account -->
          <div
            v-for="account in bankAccounts"
            :key="account.uuid"
            class="p-3 border border-gray-300 rounded bg-gray-50"
          >
            <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label class="block text-xs font-medium text-gray-600 mb-1">{{ t('club.bankAccountName') }}</label>
                <p class="text-sm font-semibold text-gray-900">{{ account.account_name }}</p>
              </div>

              <div>
                <label class="block text-xs font-medium text-gray-600 mb-1">{{ t('club.bankName') }}</label>
                <p class="text-sm text-gray-900">{{ account.bank_name }}</p>
              </div>

              <div>
                <label class="block text-xs font-medium text-gray-600 mb-1">{{ t('club.accountNumber') }}</label>
                <div class="flex items-center gap-2">
                  <p class="text-sm text-gray-900 font-mono">{{ formatAccountNumber(account.account_number) }}</p>
                  <button
                    @click="copyBankAccount(account.account_number, account.uuid)"
                    class="py-1 px-2 bg-gray-100 text-xs font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
                    :title="t('club.copyAccountNumber')"
                  >
                    {{ t('club.copy') }}
                  </button>
                </div>
                <p v-if="copySuccessStates[account.uuid]" class="text-xs text-green-600 mt-1">{{ t('club.accountNumberCopied') }}</p>
              </div>

              <div v-if="account.swift">
                <label class="block text-xs font-medium text-gray-600 mb-1">SWIFT/BIC</label>
                <p class="text-sm text-gray-900 font-mono">{{ account.swift }}</p>
              </div>

              <div>
                <label class="block text-xs font-medium text-gray-600 mb-1">{{ t('club.currency') }}</label>
                <p class="text-sm text-gray-900">{{ account.currency || 'PLN' }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Contact Section -->
      <div v-if="!loading && clubData" class="bg-white border-2 border-gray-300 rounded shadow-sm p-4 mb-4">
        <h2 class="text-lg font-semibold text-gray-800 mb-3">{{ t('club.contactData') }}</h2>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">{{ t('club.contactEmail') }}</label>
            <div class="flex items-center gap-2">
              <a :href="`mailto:${clubData.contact.email}`" class="text-sm text-blue-600 hover:underline">{{ clubData.contact.email }}</a>
              <button
                @click="copyEmail"
                class="py-1 px-2 bg-gray-100 text-xs font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
                :title="t('club.copy')"
              >
                {{ t('club.copy') }}
              </button>
            </div>
          </div>

          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">{{ t('club.contactPhone') }}</label>
            <p class="text-sm text-gray-900">{{ clubData.contact.phone }}</p>
          </div>

          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">{{ t('club.website') }}</label>
            <p class="text-sm text-blue-600">
              <a :href="clubData.contact.website_url" target="_blank" class="hover:underline">{{ clubData.contact.website }}</a>
            </p>
          </div>
        </div>
      </div>

      <!-- Social Media Section -->
      <div v-if="!loading && clubData" class="bg-white border-2 border-gray-300 rounded shadow-sm p-4 mb-4">
        <h2 class="text-lg font-semibold text-gray-800 mb-3">{{ t('club.socialMedia') }}</h2>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">{{ t('club.facebook') }}</label>
            <p class="text-sm text-blue-600">
              <a :href="clubData.social_media.facebook_url" target="_blank" class="hover:underline">{{ clubData.social_media.facebook }}</a>
            </p>
          </div>

          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">{{ t('club.instagram') }}</label>
            <p class="text-sm text-blue-600">
              <a :href="clubData.social_media.instagram_url" target="_blank" class="hover:underline">{{ clubData.social_media.instagram }}</a>
            </p>
          </div>

          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">{{ t('club.tiktok') }}</label>
            <p class="text-sm text-gray-600">{{ clubData.social_media.tiktok || '-' }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Supervisor Modal -->
    <div
      v-if="showAddSupervisorModal"
      class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      @click.self="closeAddSupervisorModal"
    >
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">{{ t('club.addSupervisor') }}</h3>

        <form @submit.prevent="saveSupervisor" class="space-y-4">
          <!-- First Name -->
          <div>
            <label for="supervisor-first-name" class="block text-sm font-medium text-gray-700 mb-1">
              {{ t('club.firstName') }} <span class="text-red-600">*</span>
            </label>
            <input
              type="text"
              id="supervisor-first-name"
              v-model="supervisorForm.first_name"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
              :class="{ 'border-red-500': getSupervisorFieldError('first_name') }"
            />
            <p v-if="getSupervisorFieldError('first_name')" class="mt-1 text-sm text-red-600">
              {{ getSupervisorFieldError('first_name') }}
            </p>
          </div>

          <!-- Last Name -->
          <div>
            <label for="supervisor-last-name" class="block text-sm font-medium text-gray-700 mb-1">
              {{ t('club.lastName') }} <span class="text-red-600">*</span>
            </label>
            <input
              type="text"
              id="supervisor-last-name"
              v-model="supervisorForm.last_name"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
              :class="{ 'border-red-500': getSupervisorFieldError('last_name') }"
            />
            <p v-if="getSupervisorFieldError('last_name')" class="mt-1 text-sm text-red-600">
              {{ getSupervisorFieldError('last_name') }}
            </p>
          </div>

          <!-- Email -->
          <div>
            <label for="supervisor-email" class="block text-sm font-medium text-gray-700 mb-1">
              {{ t('club.email') }} <span class="text-red-600">*</span>
            </label>
            <input
              type="email"
              id="supervisor-email"
              v-model="supervisorForm.email"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
              :class="{ 'border-red-500': getSupervisorFieldError('email') }"
            />
            <p v-if="getSupervisorFieldError('email')" class="mt-1 text-sm text-red-600">
              {{ getSupervisorFieldError('email') }}
            </p>
          </div>

          <!-- Phone -->
          <div>
            <label for="supervisor-phone" class="block text-sm font-medium text-gray-700 mb-1">
              {{ t('club.phone') }}
            </label>
            <input
              type="tel"
              id="supervisor-phone"
              v-model="supervisorForm.phone"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
              :class="{ 'border-red-500': getSupervisorFieldError('phone') }"
            />
            <p v-if="getSupervisorFieldError('phone')" class="mt-1 text-sm text-red-600">
              {{ getSupervisorFieldError('phone') }}
            </p>
          </div>

          <!-- Buttons -->
          <div class="flex justify-end gap-3 mt-6">
            <button
              type="button"
              @click="closeAddSupervisorModal"
              class="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded hover:bg-gray-200 border border-gray-300"
            >
              {{ t('common.cancel') }}
            </button>
            <button
              type="submit"
              :disabled="savingSupervisor"
              class="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ savingSupervisor ? t('common.saving') : t('common.save') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Remove Supervisor Modal -->
    <div
      v-if="showRemoveSupervisorModal"
      class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      @click.self="closeRemoveSupervisorModal"
    >
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">{{ t('club.removeSupervisor') }}</h3>

        <p class="text-sm text-gray-700 mb-4">
          {{ t('club.confirmRemoveSupervisor', {
            name: supervisorToRemove ? `${supervisorToRemove.first_name} ${supervisorToRemove.last_name}` : ''
          }) }}
        </p>

        <div class="flex justify-end gap-3">
          <button
            type="button"
            @click="closeRemoveSupervisorModal"
            class="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded hover:bg-gray-200 border border-gray-300"
          >
            {{ t('common.cancel') }}
          </button>
          <button
            type="button"
            @click="confirmRemoveSupervisor"
            :disabled="removingSupervisor"
            class="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ removingSupervisor ? t('common.removing') : t('common.remove') }}
          </button>
        </div>
      </div>
    </div>
  </main>
</template>
